import { NavigationState, useNavigationState } from '@react-navigation/native';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect } from 'react';
import { useIntercomContext } from '@/contexts/intercom-context';

// Intercom throttles update() to 20 calls per 30 min — debounce collapses a
// burst of navigations (tab switch, drill-in, back) into a single call.
export const INTERCOM_ROUTE_SYNC_DEBOUNCE_MS = 2000;

/**
 * Re-checks Intercom in-app message eligibility (including URL-based
 * targeting) whenever the given navigation state changes. Intercom only
 * re-evaluates eligibility on `update()`, not continuously, so without this,
 * eligibility stays frozen at boot time while navigating inside the portal
 * (SRG-341). Debounced by {@link INTERCOM_ROUTE_SYNC_DEBOUNCE_MS} so a burst
 * of navigations collapses into a single `update()` call. Takes the
 * navigation state explicitly rather than reading `useNavigationState`
 * itself, so this can be tested without mocking react-navigation.
 */
export const useIntercomRouteSync = (
  navigationState: NavigationState | undefined,
): void => {
  const { pingRouteChange } = useIntercomContext();
  const debouncedPingRouteChange = useDebouncedCallback(
    pingRouteChange,
    INTERCOM_ROUTE_SYNC_DEBOUNCE_MS,
  );

  useEffect(() => {
    debouncedPingRouteChange();

    // Only re-run when the navigation state itself changes, not when
    // debouncedPingRouteChange's identity changes across unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState]);
};

export const IntercomRouteSync: React.FC = () => {
  const navigationState = useNavigationState((state) => state);
  useIntercomRouteSync(navigationState);

  return null;
};
